import { API_ENDPOINTS, apiService } from "../api";
import { isMockApiFlag } from "../apiMode";
import {
  mockTenantAccessToken,
  MOCK_TENANT_CONTEXT_LATENCY_MS,
  MOCK_TENANT_MEMBERSHIPS,
  MOCK_TENANT_WORKSPACES,
  type TenantContextScenario,
} from "./tenantContextMock";
import type {
  MembershipRead,
  SelectTenantResponse,
  TenantWorkspaceRead,
} from "./tenantContext.types";

// Toggle: REACT_APP_TENANT_CONTEXT_API=mock → offline fixtures
const isMockTenantContextApi = (): boolean =>
  isMockApiFlag(process.env.REACT_APP_TENANT_CONTEXT_API);

const mockScenario = (): TenantContextScenario =>
  (process.env.REACT_APP_TENANT_CONTEXT_SCENARIO as TenantContextScenario) ??
  "default";

const settle = <T>(value: T): Promise<T> =>
  new Promise((resolve) =>
    setTimeout(() => resolve(value), MOCK_TENANT_CONTEXT_LATENCY_MS),
  );

/** Mirrors the interceptor's error shape so `status` branches behave as in live mode. */
const failLikeBackend = (status: number, message: string): Promise<never> =>
  new Promise((_resolve, reject) =>
    setTimeout(
      () => reject(Object.assign(new Error(message), { status })),
      MOCK_TENANT_CONTEXT_LATENCY_MS,
    ),
  );

/** Reads active memberships only; this endpoint never issues or changes a token. */
export const fetchTenantMemberships = (): Promise<MembershipRead[]> => {
  if (isMockTenantContextApi()) {
    const scenario = mockScenario();
    if (scenario === "error") {
      return failLikeBackend(500, "Organization registry is unavailable (mock).");
    }
    return settle(scenario === "empty" ? [] : MOCK_TENANT_MEMBERSHIPS);
  }
  return apiService.get<MembershipRead[]>(API_ENDPOINTS.auth.tenants);
};

/** Reads the caller's explicit workspace memberships for one selected tenant. */
export const fetchTenantWorkspaces = (
  tenantId: string,
): Promise<TenantWorkspaceRead[]> => {
  if (isMockTenantContextApi()) {
    if (mockScenario() === "error") {
      return failLikeBackend(500, "Workspace service is unavailable (mock).");
    }
    const workspaces = MOCK_TENANT_WORKSPACES[tenantId];
    // Unknown tenant id → 404, exactly as the live endpoint answers for a
    // tenant outside the caller's active memberships.
    if (!workspaces) {
      return failLikeBackend(404, "Tenant not found for this user (mock).");
    }
    return settle(workspaces);
  }
  return apiService.get<TenantWorkspaceRead[]>(
    API_ENDPOINTS.workspaces.forTenant(tenantId),
  );
};

/** Mints a fresh token scoped to `tenantId`; never issues or changes a token by itself outside this call. */
export const selectTenant = (
  tenantId: string,
): Promise<SelectTenantResponse> => {
  if (isMockTenantContextApi()) {
    if (mockScenario() === "error") {
      return failLikeBackend(500, "Could not select this organization (mock).");
    }
    const membership = MOCK_TENANT_MEMBERSHIPS.find(
      (m) => m.tenant_id === tenantId,
    );
    if (!membership) {
      return failLikeBackend(404, "Tenant not found for this user (mock).");
    }
    return settle({
      access_token: mockTenantAccessToken(tenantId),
      token_type: "bearer",
      kind: "tenant",
      tenant_id: tenantId,
      tenant_name: membership.tenant_name,
      role: membership.role_name,
      permissions: membership.permissions,
    });
  }
  return apiService.post<SelectTenantResponse>(API_ENDPOINTS.auth.selectTenant, {
    tenant_id: tenantId,
  });
};
